import { useMemo } from 'react'
import { CircleMarker, Tooltip } from 'react-leaflet'
import { useOnboarding } from '../hooks/useOnboarding'
import useStore from '../store/useStore'
import type { SiteFeature } from '../types'

interface Props {
  features: SiteFeature[]
}

/**
 * Highlights a well-known site on first visit with a pulsing ring and
 * a "Click to explore" tooltip. Dismisses the onboarding when clicked.
 * Rendered inside MapContainer.
 */
export default function StartHereIndicator({ features }: Props) {
  const { isOnboarded, dismissOnboarding } = useOnboarding()
  const openSite = useStore((s) => s.openSite)

  const target = useMemo(() => {
    if (!features.length) return null
    // Prefer Palmyra (a well-known site with strong historical + conflict context)
    return (
      features.find((f) => f.properties.name.toLowerCase().includes('palmyra')) ??
      features[0] ??
      null
    )
  }, [features])

  if (isOnboarded || !target) return null

  const [lon, lat] = target.geometry.coordinates

  return (
    <CircleMarker
      center={[lat, lon]}
      radius={22}
      className="start-here-ring"
      pathOptions={{
        color: '#e94560',
        weight: 2,
        fillColor: '#e94560',
        fillOpacity: 0.15,
      }}
      eventHandlers={{
        click: () => {
          dismissOnboarding()
          openSite(target.id)
        },
      }}
    >
      <Tooltip permanent direction="top" offset={[0, -18]} className="start-here-tooltip">
        Click to explore
      </Tooltip>
    </CircleMarker>
  )
}
